"use client"

import { Box, Flex, GridItem, Spinner } from "@chakra-ui/react"
import { ATBConfirmation } from "basket_and_checkout/shared/components/ATBConfirmation"
import { getShowAccessPassPopup } from "basket_and_checkout/shared/components/PDP/Pricing/utils"
import { PreviewAlertBox } from "components/Alert/PreviewAlert"
import { LoadingMask } from "components/LoadingMask"
import { QuizEntry } from "components/QuizEntry"
import { useLocale } from "hooks"
import { addMembershipById } from "lib/api"
import { pdpQuantityVar } from "lib/apollo/variables"
import { useRouter } from "lib/router"
import { useUser } from "lib/user"
import {
  ShadeSelectorHeader,
  ShadeSelectorModal,
  ShadeSelectorModalContent,
  ShadeSelectorModalFooter,
} from "packages/core-shopping/components/ShadeSelectorModal"
import { colours } from "@shared/ui.design-system"
import React, { useEffect, useState } from "react"
import { useSegment } from "tracking"
import type { MembershipOptionsV2, PricingToggle, ShippingType } from "types"
import { getPDPUrl } from "utils/getPDPUrl"
import { siteUrl } from "utils/getSiteUrl"
import { productView } from "utils/gtm"

import { useMultiPromoTracking } from "packages/acquisition/hooks/mutlitPromo.hooks"
import { useCurrencyCode } from "hooks/useCurrencyCode"
import { navigateTo } from "@shared/utils.routing"

import { Breadcrumbs } from "./Breadcrumbs"
import { HowToUse } from "./HowToUseSlides"
import { Reviews as PDPReviewsLayout } from "./PDPBody/Reviews"
import { PDPBodyIndex } from "./PDPBodyIndex"
import type { VariantTypeForNewPDP } from "./PDPBodyIndex/PDPBodyIndex"
import { GalleryLayout } from "./PDPHeader/PDPHeaderLayout"
import { PDPHeaderV2 } from "./PDPHeaderV2/PDPHeaderV2"
import { getMembershipIdFromQuery } from "./PDPPage.utils"
import { PDPRecommendations } from "./PDPRecommendations/PDPRecommendations"
import { ProductGalleryV2 } from "./ProductGalleryV2/ProductGalleryV2"
import { TabbedContentV2 } from "./TabbedContentV2"
import { TrialResultsWrapper } from "./TrialResults"
import type { PurchaseType } from "./types"
import { UGCVideo } from "./UGCVideo/UGCVideo"
import type { GetRecommendationType } from "packages/ui-shared/components/RecommendationModule/hooks/useRecommendations"
import { useRecommendations } from "packages/ui-shared/components/RecommendationModule/hooks/useRecommendations"
import { Logger } from "lib/logger"

const queryArguments = (
  inStockProduct: boolean,
  variant: { sku: string }
): GetRecommendationType =>
  inStockProduct
    ? {
        locationType: "PDP_IN_STOCK",
        config: {
          complementaryToSku: variant.sku,
          recommender: "constructor:pdp-complimentary",
        },
      }
    : {
        locationType: "PDP_OOS",
        config: {
          alternativeToSku: variant.sku,
          recommender: "constructor:pdp-out-of-stock-alternatives",
        },
      }

const isProduction = ["production", "test"].includes(
  process.env.NODE_ENV as string
)

export const PDPPage: React.FC<{
  variant: VariantTypeForNewPDP
  preview: boolean
  shippingInfo?: ShippingType[]
  useCloudinary?: boolean
  pricingToggle?: PricingToggle
  isOpenGate: boolean
  eventId: string
  pdpMembershipOptions?: MembershipOptionsV2[]
  membershipInBag?: boolean
}> = ({
  variant,
  preview,
  shippingInfo,
  useCloudinary = false,
  pricingToggle,
  isOpenGate,
  eventId,
  pdpMembershipOptions,
  membershipInBag,
}) => {
  const log = Logger("PDP constructor recommendations")
  const { track } = useSegment()

  const [confirmation, setConfirmation] = useState(false)
  const [purchaseType, setPurchaseType] = useState<PurchaseType>("member")
  const [isModalOpen, setIsModalOpen] = useState(false)
  const currencyCode = useCurrencyCode()

  useEffect(() => {
    const hasNonMemberPricesPreferred = getShowAccessPassPopup()
    if (hasNonMemberPricesPreferred) {
      // eslint-disable-next-line react-hooks/exhaustive-deps
      setPurchaseType("non-member")
    }
  }, [])

  const queryParams = [
    queryArguments(variant.availability.status === "instock", variant),
  ]
  const recommendations = useRecommendations(queryParams)

  if (recommendations.error) {
    log.error(
      { error: recommendations.error },
      "Failed to fetch constructor recommendations on PDP"
    )
  }
  const [{ items: { length: totalRecommendations } = [] } = {}] =
    recommendations.data?.getRecommendations.items ?? []
  const router = useRouter()
  const { membershipId, memb } = router.query
  const locale = useLocale()
  const user = useUser()

  const outOfStock = variant?.availability?.status === "outofstock"

  useEffect(() => {
    pdpQuantityVar(1)
    const {
      sku,
      title: name,
      brandName: brand,
      subTitle,
      pricing: { members, typical },
    } = variant

    track(
      {
        event: "Product Viewed",
        properties: {
          currency: locale === "en-GB" ? "GBP" : "USD",
          sku,
          name,
          brand,
          variant: subTitle || "",
          price: user.hasMembership ? members : typical,
          members_price: members,
          typical_price: typical,
          url: `${siteUrl}${getPDPUrl(variant, locale)}`,
        },
      },
      { event_id: eventId }
    )

    //The setTimeout ensures the productView event fires only after the page fully loads.
    //Without it, the event wasn't triggering at all.
    //Making this a temporary workaround that should be revisited later.
    setTimeout(() => {
      productView(variant, currencyCode, eventId)
    }, 200)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (typeof memb === "string") {
      const membershipIdFromQuery = getMembershipIdFromQuery(locale, memb)
      addMembershipById(membershipIdFromQuery)
    } else if (typeof membershipId === "string") {
      addMembershipById(membershipId)
    }
  }, [locale, memb, membershipId])

  const headerProps = {
    variant,
    setConfirmation,
    purchaseType,
    setPurchaseType,
    isModalOpen,
    setIsModalOpen,
    shippingInfo,
    pricingToggle,
    isOpenGate,
    pdpMembershipOptions,
    membershipInBag,
  }

  const { media } = variant
  const hasHowToUseSlidesMedia = variant?.howToUseSlides?.filter(
    (slide) => slide?.imageOrVideo
  ).length

  const handleShadeSelection = (shade, activeShade, event) => {
    if (("key" in event && event.key !== "Enter") || event.type !== "click")
      return

    navigateTo(getPDPUrl(shade, locale))
    track({
      event: "Page Element Clicked",
      properties: {
        module_content: `Shade Selector Modal ${activeShade.title} to ${shade.title}`,
        module_content_url: variant?.slug || "",
      },
    })
  }

  useMultiPromoTracking()

  return (
    <>
      <Flex w="full" direction="column" align="center">
        {confirmation && (
          <ATBConfirmation
            source="PDP"
            confirmation={confirmation}
            setConfirmation={setConfirmation}
            variant={variant}
          />
        )}
        {isModalOpen && (
          <ShadeSelectorModal
            isModalOpen={isModalOpen}
            onCloseModal={setIsModalOpen}
          >
            <ShadeSelectorHeader onCloseModal={setIsModalOpen}>
              Shade options
            </ShadeSelectorHeader>
            <ShadeSelectorModalContent
              variant={variant}
              handleShadeSelection={handleShadeSelection}
            />
            <ShadeSelectorModalFooter onCloseModal={setIsModalOpen} />
          </ShadeSelectorModal>
        )}
        {preview && <PreviewAlertBox />}
        <Box
          display={{ base: "block", lg: "grid" }}
          w="full"
          mb={{ base: 5, md: 6.5 }}
          maxW="90rem"
          px={{ lg: 10, xl: "7.5rem" }}
          gap={3}
          gridTemplateColumns={{
            lg: "[start] 2.1fr [content] 1fr",
          }}
          gridTemplateRows={{
            lg: "[start] auto [content] auto [end] auto",
          }}
        >
          <Box gridColumnStart={{ lg: "start" }}>
            {variant?.breadcrumbs && (
              <Breadcrumbs breadcrumbs={variant.breadcrumbs} hasActiveItem />
            )}

            <GalleryLayout>
              {media?.length && (
                <ProductGalleryV2
                  variant={variant}
                  useCloudinary={useCloudinary}
                />
              )}
            </GalleryLayout>
          </Box>
          <Box
            px={{ base: 2, md: 10, lg: 0 }}
            gridRow={{ lg: outOfStock ? "span 4" : "span 3" }}
          >
            <PDPHeaderV2 {...headerProps} />
          </Box>

          {outOfStock && (
            <PDPRecommendations
              variant={variant}
              recommendations={recommendations}
            />
          )}
          <PDPBodyIndex
            variant={variant}
            recommendationLength={totalRecommendations}
          />
          <GridItem
            px={{ base: 2, md: "80px", lg: 0 }}
            overflow="hidden"
            gridColumnStart={{ lg: "start" }}
            maxWidth="100%"
          >
            <TabbedContentV2 variant={variant} />
            {!!variant.howTo?.description?.length || hasHowToUseSlidesMedia ? (
              <HowToUse variant={variant} />
            ) : null}
            {variant?.trials?.results && (
              <TrialResultsWrapper trials={variant?.trials} />
            )}
            <QuizEntry />
            {variant?.ugc?.ugcVideoLinkDash &&
              variant?.ugc?.ugcVideoLinkHls && <UGCVideo ugc={variant.ugc} />}
          </GridItem>
        </Box>
        {!outOfStock && (
          <PDPRecommendations
            variant={variant}
            recommendations={recommendations}
          />
        )}
      </Flex>
      {isProduction && (
        <PDPReviewsLayout
          data-bv-show="reviews"
          data-bv-product-id={variant.sku}
        >
          <Box
            display="flex"
            padding="20px 0"
            alignItems="center"
            gap="20px"
            justifyContent="center"
          >
            <Spinner
              size="lg"
              thickness="4px"
              speed="0.55s"
              emptyColor={colours.secondary.light}
              color={colours.primary.dark}
            />
            Loading Reviews ...
          </Box>
        </PDPReviewsLayout>
      )}
      <LoadingMask />
    </>
  )
}
